import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from "react-router-dom";
import { useMovieContext } from '../context/MovieContext.jsx';
import { useLibraryContext } from '../context/LibraryContext.jsx';
import { useUIContext } from '../context/UIContext.jsx';
import MovieCard from '../components/MovieCard.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import Modal from "../components/Modal.jsx";
import {
  fetchMovieTrailer,
  fetchSimilarMovies,
  fetchMovieCast,
  fetchWatchProviders,
  fetchMovieImages,
  fetchMovieById,
} from "../api/tmdbApi.js";

const MovieDetailPage = ({
  handleBackToHome,
  handleMovieClick,
  handleActorClick,
}) => {
  const { selectedMovie, genresMap } = useMovieContext();
  const { userLibrary, addMovieToLibrary, removeMovieFromLibrary, openAddToCategoryModal } = useLibraryContext();
  const { showSnackbar } = useUIContext();
  const { id: movieIdParam } = useParams();
  const [localMovie, setLocalMovie] = useState(null);
  const [isLoadingLocalMovie, setIsLoadingLocalMovie] = useState(false);

  // State for data fetched by this page
  const [trailerUrl, setTrailerUrl] = useState("");
  const [isLoadingTrailer, setIsLoadingTrailer] = useState(false);
  const [trailerError, setTrailerError] = useState("");
  const [similarMovies, setSimilarMovies] = useState([]);
  const [isLoadingSimilarMovies, setIsLoadingSimilarMovies] = useState(false);
  const [similarMoviesError, setSimilarMoviesError] = useState("");
  const [similarMoviesCurrentPage, setSimilarMoviesCurrentPage] = useState(1);
  const [similarMoviesTotalPages, setSimilarMoviesTotalPages] = useState(1);
  const [topActors, setTopActors] = useState([]);
  const [isLoadingActors, setIsLoadingActors] = useState(false);
  const [actorsError, setActorsError] = useState("");
  const [watchProviders, setWatchProviders] = useState(null);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [providersError, setProvidersError] = useState("");
  const [showGoToTop, setShowGoToTop] = useState(false);

  // Poster carousel state
  const [posters, setPosters] = useState([]);
  const [isLoadingPosters, setIsLoadingPosters] = useState(false);
  const [showPosterModal, setShowPosterModal] = useState(false);
  const [activePosterIndex, setActivePosterIndex] = useState(0);

  // Fetch movie details if not present or mismatched
  useEffect(() => {
    const movieId = movieIdParam || (selectedMovie && selectedMovie.id);
    if (!movieId) return;
    if (!selectedMovie || selectedMovie.id !== movieId) {
      setIsLoadingLocalMovie(true);
      fetchMovieById(movieId, genresMap)
        .then(({ data, error }) => {
          if (!error && data) {
            setLocalMovie(data);
          }
        })
        .finally(() => setIsLoadingLocalMovie(false));
    } else {
      setLocalMovie(selectedMovie);
    }
  }, [movieIdParam, selectedMovie, genresMap]);

  // Use localMovie for all rendering
  const movie = localMovie;

  useEffect(() => {
    // Fetch all data when the component mounts with a movie
    if (movie) {
      window.scrollTo(0, 0); // Scroll to top on new movie selection

      // Fetch trailer
      const loadTrailer = async () => {
        setIsLoadingTrailer(true);
        setTrailerError('');
        const { data, error } = await fetchMovieTrailer(movie.id);
        if (error) {
          setTrailerError(error);
          showSnackbar(error, error === 'No trailer available.' ? 'warning' : 'error');
        } else {
          setTrailerUrl(data);
        }
        setIsLoadingTrailer(false);
      };

      // Fetch cast
      const loadCast = async () => {
        setIsLoadingActors(true);
        setActorsError('');
        const { data, error } = await fetchMovieCast(movie.id);
        if (error) {
          setActorsError(error);
          showSnackbar(error, 'error');
        } else {
          setTopActors(data);
        }
        setIsLoadingActors(false);
      };

      // Fetch similar movies
      const loadSimilarMovies = async () => {
        setIsLoadingSimilarMovies(true);
        setSimilarMoviesError('');
        const { data, error } = await fetchSimilarMovies({
          movieId: movie.id,
          page: 1,
          genresMap,
        });
        if (error) {
          setSimilarMoviesError(error);
          showSnackbar(error, 'error');
        } else {
          setSimilarMovies(data.movies);
          setSimilarMoviesCurrentPage(data.page);
          setSimilarMoviesTotalPages(data.totalPages);
        }
        setIsLoadingSimilarMovies(false);
      };

      // Fetch watch providers
      const loadWatchProviders = async () => {
        setIsLoadingProviders(true);
        setProvidersError('');
        const { data, error } = await fetchWatchProviders(movie.id);
        if (error) {
          setProvidersError(error);
        } else {
          setWatchProviders(data);
        }
        setIsLoadingProviders(false);
      };

      // Fetch posters
      const loadPosters = async () => {
        setIsLoadingPosters(true);
        const { data, error } = await fetchMovieImages(movie.id);
        if (!error && data) {
          setPosters(data);
        }
        setIsLoadingPosters(false);
      };

      // Execute all fetches
      loadTrailer();
      loadCast();
      loadSimilarMovies();
      loadWatchProviders();
      loadPosters();
    }
  }, [movie, genresMap, showSnackbar]);

  useEffect(() => {
    const handleScroll = () => {
      setShowGoToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLoadMoreSimilarMovies = useCallback(async () => {
    if (!movie) return;

    setIsLoadingSimilarMovies(true);
    const { data, error } = await fetchSimilarMovies({
      movieId: movie.id,
      page: similarMoviesCurrentPage + 1,
      genresMap,
    });

    if (error) {
      setSimilarMoviesError(error);
      showSnackbar(error, 'error');
    } else {
      // Append to existing movies
      setSimilarMovies(prev => [...prev, ...data.movies]);
      setSimilarMoviesCurrentPage(data.page);
      setSimilarMoviesTotalPages(data.totalPages);
    }
    setIsLoadingSimilarMovies(false);
  }, [movie, similarMoviesCurrentPage, genresMap, showSnackbar]);

  const handleGoToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openPosterModal = (index) => {
    setActivePosterIndex(index);
    setShowPosterModal(true);
  };

  const closePosterModal = () => {
    setShowPosterModal(false);
  };

  const showPrevPoster = () => {
    setActivePosterIndex((prevIndex) =>
      prevIndex === 0 ? posters.length - 1 : prevIndex - 1
    );
  };

  const showNextPoster = () => {
    setActivePosterIndex((prevIndex) =>
      prevIndex === posters.length - 1 ? 0 : prevIndex + 1
    );
  };

  if (isLoadingLocalMovie || !movie) {
    return <LoadingSpinner message="Loading movie details..." />;
  }

  return (
    <section className="mb-12">
      <button
        onClick={handleBackToHome}
        className="mb-8 bg-[#141414] hover:bg-[#333] text-white font-bold py-2 px-6 transition duration-300 ease-in-out flex items-center shadow-md"
      >
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          ></path>
        </svg>
        Back to Home
      </button>

      <div
        className="relative overflow-hidden shadow-2xl mb-8"
        style={{
          backgroundImage: movie.backdrop
            ? `linear-gradient(to top, rgba(3, 7, 18, 1), rgba(3, 7, 18, 0.7)), url(${movie.backdrop})`
            : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "400px",
          width: "100vw",
          marginLeft: "calc(-50vw + 50%)",
          marginRight: "calc(-50vw + 50%)",
          display: "flex",
          alignItems: "flex-end",
          padding: "1.5rem",
          borderRadius: 0,
        }}
      >
        <div className="flex flex-col md:flex-row items-center md:items-end text-white relative z-10 w-full">
          <img
            src={movie.poster}
            alt={movie.title}
            className="w-32 h-48 md:w-48 md:h-72 object-cover rounded-lg shadow-xl border-4 border-gray-700 flex-shrink-0 mb-6 md:mb-0 md:mr-6"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://placehold.co/150x225/374151/D1D5DB?text=No+Poster`;
            }}
          />
          <div className="text-center md:text-left flex-grow">
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-pink-500 leading-tight">
              {movie.title}{" "}
              <span className="font-light text-gray-300">({movie.year})</span>
            </h2>
            <p className="text-gray-300 text-xl mb-4 flex items-center justify-center md:justify-start">
              {movie.genres.join(", ")}
              {movie.rating !== "N/A" && (
                <span className="ml-4 text-yellow-400 text-2xl flex items-center">
                  <svg
                    className="w-6 h-6 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.683-1.539 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.565-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z"></path>
                  </svg>
                  {movie.rating}
                </span>
              )}
            </p>
            <p className="text-gray-200 text-lg mb-6 leading-relaxed">
              {movie.description}
            </p>

            {/* Watch Providers Section */}
            <div className="mb-8">
              <h4 className="text-xl font-bold text-white mb-2">
                Where to Watch
              </h4>
              {isLoadingProviders ? (
                <LoadingSpinner size="h-6 w-6" />
              ) : providersError ? (
                <p className="text-red-400 text-md">{providersError}</p>
              ) : watchProviders ? (
                <div className="space-y-2">
                  {watchProviders.link && (
                    <a
                      href={watchProviders.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mb-2 bg-[#e50914] hover:bg-[#b0060f] text-white font-bold py-2 px-6 rounded transition duration-300 ease-in-out shadow-md"
                    >
                      <svg
                        className="w-5 h-5 inline-block mr-2 -mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M14 3v4a1 1 0 001 1h4m-5 13h6a2 2 0 002-2V7a2 2 0 00-2-2h-6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        ></path>
                      </svg>
                      Watch Now
                    </a>
                  )}
                  {watchProviders.flatrate &&
                    watchProviders.flatrate.length > 0 && (
                      <div>
                        <span className="font-semibold text-green-400">
                          Streaming:
                        </span>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {watchProviders.flatrate.map((provider) => (
                            <a
                              key={provider.provider_id}
                              href={watchProviders.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-2 bg-gray-800 rounded px-3 py-1 hover:bg-gray-700 transition-colors duration-200"
                            >
                              {provider.logo_path && (
                                <img
                                  src={`https://image.tmdb.org/t/p/w45${provider.logo_path}`}
                                  alt={provider.provider_name}
                                  className="w-6 h-6 rounded"
                                />
                              )}
                              <span className="text-white text-sm">
                                {provider.provider_name}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  {watchProviders.rent && watchProviders.rent.length > 0 && (
                    <div>
                      <span className="font-semibold text-yellow-400">
                        Rent:
                      </span>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {watchProviders.rent.map((provider) => (
                          <a
                            key={provider.provider_id}
                            href={watchProviders.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 bg-gray-800 rounded px-3 py-1 hover:bg-gray-700 transition-colors duration-200"
                          >
                            {provider.logo_path && (
                              <img
                                src={`https://image.tmdb.org/t/p/w45${provider.logo_path}`}
                                alt={provider.provider_name}
                                className="w-6 h-6 rounded"
                              />
                            )}
                            <span className="text-white text-sm">
                              {provider.provider_name}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {watchProviders.buy && watchProviders.buy.length > 0 && (
                    <div>
                      <span className="font-semibold text-blue-400">Buy:</span>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {watchProviders.buy.map((provider) => (
                          <a
                            key={provider.provider_id}
                            href={watchProviders.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 bg-gray-800 rounded px-3 py-1 hover:bg-gray-700 transition-colors duration-200"
                          >
                            {provider.logo_path && (
                              <img
                                src={`https://image.tmdb.org/t/p/w45${provider.logo_path}`}
                                alt={provider.provider_name}
                                className="w-6 h-6 rounded"
                              />
                            )}
                            <span className="text-white text-sm">
                              {provider.provider_name}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {!watchProviders.flatrate &&
                    !watchProviders.rent &&
                    !watchProviders.buy && (
                      <p className="text-gray-400">
                        No streaming sources found for this movie.
                      </p>
                    )}
                </div>
              ) : (
                <p className="text-gray-400">
                  No streaming sources found for this movie.
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-center md:justify-start space-y-3 sm:space-y-0 sm:space-x-4">
              {!userLibrary.some((m) => m.id === movie.id) ? (
                <button
                  onClick={() => addMovieToLibrary(movie)}
                  className="bg-[#e50914] hover:bg-[#b0060f] text-white font-bold py-3 px-6 transition duration-300 ease-in-out transform hover:scale-105 shadow-md flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    ></path>
                  </svg>
                  Add to Library
                </button>
              ) : (
                <>
                  <button
                    onClick={() => openAddToCategoryModal(movie)}
                    className="bg-[#e50914] hover:bg-[#b0060f] text-white font-bold py-3 px-6 transition duration-300 ease-in-out transform hover:scale-105 shadow-md flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 7h.01M7 3h5.989A2 2 0 0115 5v4a2 2 0 01-2 2h-3L7 15V9a2 2 0 00-2-2zm0 0H4a2 2 0 00-2 2v6l3 3h5.989a2 2 0 002-2V9a2 2 0 00-2-2h-3.01z"
                      ></path>
                    </svg>
                    Manage Categories
                  </button>
                  <button
                    onClick={() => removeMovieFromLibrary(movie)}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 transition duration-300 ease-in-out transform hover:scale-105 shadow-md flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      ></path>
                    </svg>
                    Remove from Library
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Place carousel here, just above the trailer section */}
      <h3 className="text-3xl font-extrabold text-white mb-6">Posters</h3>
      {isLoadingPosters ? (
        <LoadingSpinner message="Loading posters..." />
      ) : posters.length > 0 ? (
        <div className="flex overflow-x-auto space-x-4 mb-8 pb-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900">
          {posters.map((url, idx) => (
            <img
              key={url}
              src={url}
              alt={`Poster ${idx + 1}`}
              className="h-40 w-auto rounded-lg shadow-lg cursor-pointer hover:scale-105 transition-transform duration-200 border-2 border-gray-700"
              onClick={() => openPosterModal(idx)}
            />
          ))}
        </div>
      ) : (
        <p className="text-gray-400 mb-8">No posters found for this movie.</p>
      )}
      {/* Poster Modal */}
      <Modal
        show={showPosterModal}
        onClose={closePosterModal}
        title={null}
        maxWidth="max-w-3xl"
      >
        {posters.length > 0 && (
          <div className="flex flex-col items-center">
            <div className="relative w-full flex items-center justify-center">
              <button
                onClick={showPrevPoster}
                className="absolute left-0 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-80 text-white p-2 rounded-full z-10"
                style={{ left: 0 }}
                aria-label="Previous poster"
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <img
                src={posters[activePosterIndex]}
                alt={`Poster ${activePosterIndex + 1}`}
                className="max-h-[70vh] w-auto rounded-lg shadow-2xl border-4 border-gray-700 mx-auto"
                style={{ maxWidth: "90vw" }}
              />
              <button
                onClick={showNextPoster}
                className="absolute right-0 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-80 text-white p-2 rounded-full z-10"
                style={{ right: 0 }}
                aria-label="Next poster"
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
            <div className="text-gray-300 mt-4">
              Poster {activePosterIndex + 1} of {posters.length}
            </div>
          </div>
        )}
      </Modal>

      <h3 className="text-3xl font-extrabold text-white mb-6">Trailer</h3>
      <div className="aspect-video w-full bg-gray-800 rounded-lg overflow-hidden shadow-xl flex items-center justify-center mb-12">
        {isLoadingTrailer ? (
          <LoadingSpinner message="Loading trailer..." />
        ) : trailerError ? (
          <p className="text-red-400 text-lg text-center p-4">{trailerError}</p>
        ) : trailerUrl ? (
          <iframe
            width="100%"
            height="100%"
            src={trailerUrl}
            title={`${movie.title} trailer`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        ) : (
          <p className="text-gray-400 text-lg text-center p-4">
            No trailer found for this movie.
          </p>
        )}
      </div>

      {/* Main Cast Section */}
      <h3 className="text-3xl font-extrabold text-white mb-6">Main Cast</h3>
      {isLoadingActors ? (
        <LoadingSpinner message="Loading cast..." />
      ) : actorsError ? (
        <p className="text-center text-red-500 text-lg mt-8">{actorsError}</p>
      ) : topActors.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
          {topActors.map((actor) => (
            <div
              key={actor.id}
              className="flex flex-col items-center text-center cursor-pointer hover:scale-105 transition-transform duration-200"
              onClick={() => handleActorClick(actor.id)}
            >
              <img
                src={actor.profile_path}
                alt={actor.name}
                className="w-24 h-24 object-cover rounded-full mb-2 border-2 border-gray-700"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://placehold.co/100x100/374151/D1D5DB?text=No+Pic`;
                }}
              />
              <p className="font-bold text-white text-md">{actor.name}</p>
              <p className="text-gray-400 text-sm">as {actor.character}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-400 text-lg mt-8">
          No cast information available.
        </p>
      )}

      {/* Similar Movies Section */}
      <h3 className="text-3xl font-extrabold text-white mb-6">
        Similar Movies
      </h3>
      {isLoadingSimilarMovies && similarMovies.length === 0 ? (
        <LoadingSpinner message="Loading similar movies..." />
      ) : similarMoviesError ? (
        <p className="text-center text-red-500 text-lg mt-8">
          {similarMoviesError}
        </p>
      ) : similarMovies.length > 0 ? (
        <div className="flex justify-center w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {similarMovies.map((movie) => (
              <MovieCard
                key={`similar-${movie.id}`}
                movie={movie}
                onCardClick={handleMovieClick}
                onAddToLibrary={addMovieToLibrary}
                onRemoveFromLibrary={removeMovieFromLibrary}
                userLibrary={userLibrary}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="text-center text-gray-400 text-lg mt-8">
          No similar movies found for this title.
        </p>
      )}

      {/* Load More Similar Movies Button */}
      {!isLoadingSimilarMovies &&
        similarMovies.length > 0 &&
        similarMoviesCurrentPage < similarMoviesTotalPages && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleLoadMoreSimilarMovies}
              className="bg-[#e50914] hover:bg-[#b0060f] text-white font-bold py-3 px-8 transition duration-300 ease-in-out transform hover:scale-105 shadow-lg"
            >
              Load More Similar Movies
            </button>
          </div>
        )}
      {isLoadingSimilarMovies && similarMovies.length > 0 && (
        <div className="flex justify-center mt-8">
          <LoadingSpinner size="h-8 w-8" />
        </div>
      )}

      {showGoToTop && (
        <button
          onClick={handleGoToTop}
          className="fixed bottom-8 right-8 z-50 bg-[#e50914] hover:bg-[#b0060f] text-white p-4 rounded-full shadow-lg transition duration-300 ease-in-out flex items-center justify-center animate-fade-in-up"
          title="Go to Top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 15l7-7 7 7"
            />
          </svg>
        </button>
      )}
    </section>
  );
};

export default React.memo(MovieDetailPage);
